import { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../../../config';
import { generateOfferNumber } from '../utils';

const HISTORY_ITEMS_PER_PAGE = 12;

const EMPTY_FILTERS = {
  status: 'all',
  clientSearch: '',
  offerNumberSearch: '',
  dateFrom: '',
  dateTo: '',
  minPrice: '',
  maxPrice: ''
};

export default function useOffersHistory({ setMessage, setError, scrollToTop }) {
  const [offersHistory, setOffersHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyCurrentPage, setHistoryCurrentPage] = useState(1);
  const [historyFilters, setHistoryFilters] = useState(EMPTY_FILTERS);

  const [showOfferModal, setShowOfferModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [isEditingOfferModal, setIsEditingOfferModal] = useState(false);
  const [savingOfferModal, setSavingOfferModal] = useState(false);

  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [offerToDelete, setOfferToDelete] = useState(null);

  const fetchOffersHistory = async () => {
    setLoadingHistory(true);
    setHistoryError('');
    try {
      const res = await axios.get(`${API_BASE}/api/sent-offers`, { withCredentials: true });
      setOffersHistory(res.data || []);
    } catch {
      setHistoryError('Nie udało się pobrać historii ofert');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleDeleteOffer = async (offerId) => {
    if (!offerId) return;
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      await axios.delete(`${API_BASE}/api/sent-offers/${offerId}`, {
        withCredentials: true,
        headers: { 'X-CSRF-Token': csrf }
      });
      setOffersHistory(prev => prev.filter(o => o.id !== offerId));
      setSelectedOffer(prev => (prev?.id === offerId ? null : prev));
      setShowOfferModal(prev => (selectedOffer?.id === offerId ? false : prev));
    } catch {
      setHistoryError('Nie udało się usunąć oferty');
      scrollToTop();
    }
  };

  const promptDeleteOffer = (offer) => {
    setOfferToDelete(offer || null);
    setShowDeleteConfirmModal(Boolean(offer));
  };

  const confirmDeleteFromModal = async () => {
    if (!offerToDelete) return;
    await handleDeleteOffer(offerToDelete.id);
    setShowDeleteConfirmModal(false);
    setOfferToDelete(null);
  };

  const openOfferModal = (offer) => {
    setSelectedOffer(offer);
    setShowOfferModal(true);
    setIsEditingOfferModal(false);
  };

  const getOfferNumber = (offer) => {
    const direct = String(offer?.offer_number || offer?.offerNumber || '').trim();
    if (direct) return direct;
    return generateOfferNumber(offer?.offer_seq || offer?.id, offer?.created_at);
  };

  const getFilteredOffers = () => {
    return offersHistory.filter(offer => {
      if (historyFilters.status === 'sent' && !offer.is_sent) return false;
      if (historyFilters.status === 'draft' && offer.is_sent) return false;

      if (historyFilters.offerNumberSearch) {
        const searchTerm = historyFilters.offerNumberSearch.toLowerCase();
        const offerNumber = getOfferNumber(offer);
        if (!offerNumber.toLowerCase().includes(searchTerm)) return false;
      }

      if (historyFilters.clientSearch) {
        const searchLower = historyFilters.clientSearch.toLowerCase();
        const clientMatch = (offer.client_name || '').toLowerCase().includes(searchLower);
        const emailMatch = (offer.email || '').toLowerCase().includes(searchLower);
        if (!clientMatch && !emailMatch) return false;
      }

      if (historyFilters.dateFrom) {
        const offerDate = new Date(offer.date_from);
        const filterDate = new Date(historyFilters.dateFrom);
        if (offerDate < filterDate) return false;
      }

      if (historyFilters.dateTo) {
        const offerDate = new Date(offer.date_to);
        const filterDate = new Date(historyFilters.dateTo);
        if (offerDate > filterDate) return false;
      }

      if (historyFilters.minPrice) {
        const price = parseFloat(offer.total_price || 0);
        const minPrice = parseFloat(historyFilters.minPrice);
        if (price < minPrice) return false;
      }

      if (historyFilters.maxPrice) {
        const price = parseFloat(offer.total_price || 0);
        const maxPrice = parseFloat(historyFilters.maxPrice);
        if (price > maxPrice) return false;
      }

      return true;
    });
  };

  const clearHistoryFilters = () => setHistoryFilters(EMPTY_FILTERS);

  const hasActiveFilters = () =>
    historyFilters.status !== 'all' ||
    historyFilters.clientSearch ||
    historyFilters.offerNumberSearch ||
    historyFilters.dateFrom ||
    historyFilters.dateTo ||
    historyFilters.minPrice ||
    historyFilters.maxPrice;

  // Reset page when filters change
  useEffect(() => {
    setHistoryCurrentPage(1);
  }, [historyFilters]);

  const filteredOffers = getFilteredOffers();
  const historyTotalPages = Math.ceil(filteredOffers.length / HISTORY_ITEMS_PER_PAGE);
  const historyStartIdx = (historyCurrentPage - 1) * HISTORY_ITEMS_PER_PAGE;
  const paginatedFilteredOffers = filteredOffers.slice(historyStartIdx, historyStartIdx + HISTORY_ITEMS_PER_PAGE);

  return {
    offersHistory,
    setOffersHistory,
    loadingHistory,
    historyError,
    historyCurrentPage,
    setHistoryCurrentPage,
    historyFilters,
    setHistoryFilters,
    HISTORY_ITEMS_PER_PAGE,
    showOfferModal,
    setShowOfferModal,
    selectedOffer,
    setSelectedOffer,
    isEditingOfferModal,
    setIsEditingOfferModal,
    savingOfferModal,
    setSavingOfferModal,
    showDeleteConfirmModal,
    setShowDeleteConfirmModal,
    offerToDelete,
    setOfferToDelete,
    fetchOffersHistory,
    handleDeleteOffer,
    promptDeleteOffer,
    confirmDeleteFromModal,
    openOfferModal,
    getOfferNumber,
    filteredOffers,
    historyTotalPages,
    paginatedFilteredOffers,
    clearHistoryFilters,
    hasActiveFilters,
  };
}
